/**
 * Rysunek dachu z Projektu 3D: rzut dachu z góry i osobny arkusz każdej połaci z modułami w prawdziwych wymiarach
 * (rzut z góry skraca długości wzdłuż spadku, a ekipa montuje na połaci). Dane z `lib/projekt3d/rzut.js`.
 *
 * Wzór: `a3DACH.pdf` z projektu właściciela. Wymiary łańcuchowe nad rzutem i z prawej (na arkuszu połaci pod
 * połacią, od strony okapu, i z prawej), na zewnątrz wymiar całkowity. Rzędy modułów o tym samym układzie dzielą
 * jedno pasmo; inne rzędy i przeszkody dostają kolejne pasma (najwyżej trzy, pełne wymiary są na arkuszach połaci).
 * Bez adresu i podkładu mapy (decyzja właściciela 2026-10-23), bez kabli (decyzja 2026-10-23).
 *
 * `opisRysunkuDachu` jest czysta (testy): skala, położenie, pasma wymiarów, legenda, tabelka. `narysujRysunekDachu`
 * rysuje opis w pdf-lib, `rysujRysunekDachu` ładuje pdf-lib dopiero przy generowaniu.
 */
import { ukladArkusza, dobierzSkale, mmNaMetr, dataRysunku, FORMAT_DOMYSLNY } from './arkusz'
import { pasmaWymiarow, rozkladLancucha, wysokoscPasma, TOLERANCJA } from './wymiary'
import {
  rysujModul, rysujPrzeszkodeRodzaju,
  rysujStrefe, rysujOdstep, rysujStrzalke, rysujPolnoc, szerokoscCyfr, srodekWielokata, PASY,
} from './pdf'
import { DOPISEK_RYSUNKU } from '../../config/rysunki'
import { ponumerujArkusze, narysujArkusze, rysujArkusze } from './arkusze'
import { RAMA_MODULU } from '../projekt3d/rzut'

// Pas wokół rysunku na opisy połaci (rzut) i krawędzi (arkusz połaci), mm na papierze.
const PIERSCIEN = 10
const PIERSCIEN_POLACI = 6
// Pasm poza wymiarem całkowitym: na rzucie dwa (położenie), na arkuszu połaci cztery (montaż).
const MAKS_PASM_RZUTU = 2
const MAKS_PASM_POLACI = 4
const SIN5 = Math.sin((5 * Math.PI) / 180)
const DLUGOSC_STRZALKI = 3.8

const ZNANE_PRZESZKODY = { komin: 'Komin', okno_dachowe: 'Okno dachowe', pod_dachem: 'Część połaci pod wyższym dachem' }
// Rodzaje z własnym symbolem; pozostałe (także nieznane) rysujemy symbolem ogólnym „Przeszkoda”.
const SYMBOLE = new Set(['komin', 'okno_dachowe', 'pod_dachem', 'wylaz', 'antena', 'wentylacja', 'lukarna', ...PASY])
const NAZWY_KRAWEDZI = { okap: 'okap', kalenica: 'kalenica', naroze: 'naroże', szczytowa: 'krawędź szczytowa', zalamanie: 'załamanie', kosz: 'kosz' }
// Poprzeczki ławy kominiarskiej na rysunku co ok. 40 cm w terenie.
const KROK_PASA = 0.4

const liczba = (x, miejsc = 2) => x.toLocaleString('pl-PL', { minimumFractionDigits: miejsc, maximumFractionDigits: miejsc })
export const modulow = (n) => {
  if (n === 1) return 'moduł'
  const j = n % 10
  const d = n % 100
  return j >= 2 && j <= 4 && (d < 12 || d > 14) ? 'moduły' : 'modułów'
}
const suma = (xs) => xs.reduce((s, x) => s + x, 0)
const zakresPunktow = (punkty) => ({
  minX: Math.min(...punkty.map((p) => p[0])), maxX: Math.max(...punkty.map((p) => p[0])),
  minY: Math.min(...punkty.map((p) => p[1])), maxY: Math.max(...punkty.map((p) => p[1])),
})

/* ═══ Rozmieszczenie arkusza ═══════════════════════════════════════════════════════════════════════ */

// Pasma z wierszy i na końcu (najdalej od rysunku) wymiar całkowity, chyba że ostatnie pasmo już nim jest.
const zCalkowitym = (pasma, lo, hi) => {
  const ost = pasma[pasma.length - 1]
  const calkowity = ost && ost.length === 2 && Math.abs(ost[0] - lo) <= TOLERANCJA && Math.abs(ost[1] - hi) <= TOLERANCJA
  return calkowity ? pasma : [...pasma, [lo, hi]]
}

/**
 * Skala i położenie obiektu `zakres` (metry, Y w dół) z pasmami wymiarów: poziome nad obiektem albo pod nim
 * (`naDole`), pionowe z prawej. `wiersze(skala)` daje { wierszeX, wierszeY } (przy drobnej skali rzut skraca
 * łańcuchy). Zwraca przeliczenie na papier i łańcuchy z położeniem napisów.
 */
export const rozmiescArkusz = ({ format, zakres, wiersze, naDole = false, maks = 3, pierscien = PIERSCIEN }) => {
  const uklad = ukladArkusza(format)
  const W = Math.max(zakres.maxX - zakres.minX, 0.01)
  const H = Math.max(zakres.maxY - zakres.minY, 0.01)
  const pasma = (skala) => {
    const { wierszeX, wierszeY } = wiersze(skala)
    return {
      X: zCalkowitym(pasmaWymiarow(wierszeX, [zakres.minX, zakres.maxX], { maks }), zakres.minX, zakres.maxX),
      Y: zCalkowitym(pasmaWymiarow(wierszeY, [zakres.minY, zakres.maxY], { maks }), zakres.minY, zakres.maxY),
    }
  }
  const grubosci = (lista, k) => lista.map((punkty) => wysokoscPasma(rozkladLancucha({ punkty, naPapier: (m) => m * k, szerokosc: szerokoscCyfr }).poziomy))
  const potrzebne = (skala) => {
    const k = mmNaMetr(skala)
    const p = pasma(skala)
    return { w: W * k + 2 * pierscien + suma(grubosci(p.Y, k)), h: H * k + 2 * pierscien + suma(grubosci(p.X, k)) }
  }
  const dobor = dobierzSkale(potrzebne, uklad.rysunek)
  let k = mmNaMetr(dobor.skala)
  const { X: pasmaX, Y: pasmaY } = pasma(dobor.skala)
  if (!dobor.miesci) {
    // Bardzo duży obiekt: rysunek bez skali, dopasowany do arkusza.
    k = Math.min((uklad.rysunek.w - 2 * pierscien - suma(grubosci(pasmaY, k))) / W, (uklad.rysunek.h - 2 * pierscien - suma(grubosci(pasmaX, k))) / H)
  }
  const gX = grubosci(pasmaX, k)
  const gY = grubosci(pasmaY, k)
  const calosc = { w: W * k + 2 * pierscien + suma(gY), h: H * k + 2 * pierscien + suma(gX) }
  const lewo = uklad.rysunek.x + Math.max(0, (uklad.rysunek.w - calosc.w) / 2)
  const gora = uklad.rysunek.y + Math.max(0, (uklad.rysunek.h - calosc.h) / 2)
  const x0 = lewo + pierscien
  const y0 = gora + (naDole ? 0 : suma(gX)) + pierscien
  const naPapierX = (m) => x0 + (m - zakres.minX) * k
  const naPapierY = (m) => y0 + (m - zakres.minY) * k
  let polozenie = naDole ? y0 + H * k + pierscien : y0 - pierscien
  const lancuchyX = pasmaX.map((punkty, i) => {
    const l = { polozenie, rozklad: rozkladLancucha({ punkty, naPapier: naPapierX, szerokosc: szerokoscCyfr }) }
    polozenie += (naDole ? 1 : -1) * gX[i]
    return l
  })
  polozenie = x0 + W * k + pierscien
  const lancuchyY = pasmaY.map((punkty, i) => {
    const l = { polozenie, rozklad: rozkladLancucha({ punkty, naPapier: naPapierY, szerokosc: szerokoscCyfr }) }
    polozenie += gY[i]
    return l
  })
  return {
    uklad,
    skala: dobor.miesci ? dobor.skala : null,
    k,
    naPapierX,
    naPapierY,
    naPapier: ([x, y]) => [naPapierX(x), naPapierY(y)],
    obiekt: { x: x0, y: y0, w: W * k, h: H * k },
    calosc: { x: lewo, y: gora, ...calosc },
    lancuchyX,
    stronaX: naDole ? 1 : -1,
    lancuchyY,
  }
}

/* ═══ Treść ════════════════════════════════════════════════════════════════════════════════════════ */

const opisModulu = (m) => {
  const nazwa = m.nazwa || 'Moduł'
  const moc = /\d\s*W\b/.test(nazwa) ? '' : ` ${Math.round(m.moc)} W`
  return `Moduł fotowoltaiczny ${nazwa}${moc}, ${Math.round(m.dlugosc * 1000)} × ${Math.round(m.szerokosc * 1000)} mm, z numerem`
}

const legenda = (polacie, { modul, spadek = false, odstep = null, bezPodDachem = false }) => {
  const pozycje = []
  if (polacie.some((p) => p.moduly.length)) pozycje.push({ symbol: 'modul', opis: opisModulu(modul) })
  const rodzaje = new Map()
  for (const p of polacie) {
    for (const o of p.przeszkody) if (!rodzaje.has(o.rodzaj) && !(bezPodDachem && o.rodzaj === 'pod_dachem')) rodzaje.set(o.rodzaj, o.nazwa)
  }
  for (const [rodzaj, nazwa] of rodzaje) {
    pozycje.push({ symbol: SYMBOLE.has(rodzaj) ? rodzaj : 'przeszkoda', opis: ZNANE_PRZESZKODY[rodzaj] || nazwa || 'Przeszkoda' })
  }
  if (polacie.some((p) => p.przeszkody.some((o) => o.strefaUv && o.rodzaj !== 'pod_dachem'))) pozycje.push({ symbol: 'strefa', opis: 'Strefa bez modułów przy przeszkodzie' })
  if (odstep != null) pozycje.push({ symbol: 'odstep', opis: `Granica pola modułów: ${Math.round(odstep * 100)} cm od krawędzi połaci` })
  if (spadek) pozycje.push({ symbol: 'spadek', opis: 'Spadek połaci: litera połaci i kąt nachylenia' })
  return pozycje
}

const wierszPolaci = (p) => {
  const n = p.moduly.length
  const ile = `${n} ${modulow(n)}`
  if (p.grunt) return `Konstrukcja na gruncie: ${ile}`
  if (p.plaska) return `Połać ${p.litera}: dach płaski, ${ile}`
  return `Połać ${p.litera}: ${p.kierunek}, azymut ${p.azymut}°, kąt ${p.nachylenie}°, ${ile}`
}

const wierszInstalacji = (dane) => `${dane.liczba} ${modulow(dane.liczba)} × ${Math.round(dane.modul.moc)} W = ${liczba(dane.mocKwp)} kWp`

// Kierunek wzdłuż rzędu modułu na rysunku (pierwszy bok obrysu leży wzdłuż osi u modułu).
const kierunekRzedu = (m) => {
  const dx = m.plan[1][0] - m.plan[0][0]
  const dy = m.plan[1][1] - m.plan[0][1]
  const d = Math.hypot(dx, dy) || 1
  return [dx / d, dy / d]
}

/**
 * Wiersze wymiarów rzutu: dla każdej połaci z modułami rzędy wzdłuż osi, do której są równoległe (krawędzie
 * modułów), a w poprzek jeden wiersz z pasami rzędów i zasięgiem połaci; potem przeszkody (połacie z modułami
 * najpierw). Połacie ukośne do kartki dają tylko zasięg pola modułów.
 */
export const wierszeRzutu = (dane, { zwarte = false } = {}) => {
  const wierszeX = []
  const wierszeY = []
  const przeszkodyX = []
  const przeszkodyY = []
  for (const p of dane.polacie) {
    if (!p.moduly.length) continue
    const rzedy = new Map()
    for (const m of p.moduly) rzedy.set(m.rzad, [...(rzedy.get(m.rzad) || []), m])
    const [dx, dy] = kierunekRzedu(p.moduly[0])
    const widoczne = (p.czesci || []).flat()
    const zakresPolaci = widoczne.length ? zakresPunktow(widoczne) : null
    const krawedzie = (ms, os) => ms.flatMap((m) => [Math.min(...m.plan.map((q) => q[os])), Math.max(...m.plan.map((q) => q[os]))])
    const pasRzedu = (ms, os) => [Math.min(...krawedzie(ms, os)), Math.max(...krawedzie(ms, os))]
    // Zwarte: ciągi modułów stykających się w rzędzie jako jeden odcinek (przerwy po przeszkodach zostają).
    const wzdluz = (ms, os) => {
      if (!zwarte) return krawedzie(ms, os)
      const odcinki = ms.map((m) => [Math.min(...m.plan.map((q) => q[os])), Math.max(...m.plan.map((q) => q[os]))]).sort((a, b) => a[0] - b[0])
      const ciagi = []
      for (const o of odcinki) {
        const ost = ciagi[ciagi.length - 1]
        if (ost && o[0] - ost[1] < 0.1) ost[1] = Math.max(ost[1], o[1])
        else ciagi.push([...o])
      }
      return ciagi.flat()
    }
    const wPoprzek = (os) => [
      ...[...rzedy.values()].flatMap((ms) => pasRzedu(ms, os)),
      ...(zakresPolaci ? (os === 0 ? [zakresPolaci.minX, zakresPolaci.maxX] : [zakresPolaci.minY, zakresPolaci.maxY]) : []),
    ]
    if (Math.abs(dy) < SIN5) {
      for (const ms of rzedy.values()) wierszeX.push(wzdluz(ms, 0))
      wierszeY.push(wPoprzek(1))
    } else if (Math.abs(dx) < SIN5) {
      for (const ms of rzedy.values()) wierszeY.push(wzdluz(ms, 1))
      wierszeX.push(wPoprzek(0))
    } else {
      wierszeX.push(pasRzedu(p.moduly, 0))
      wierszeY.push(pasRzedu(p.moduly, 1))
    }
  }
  const zPrzeszkod = (polacie) => {
    for (const p of polacie) {
      for (const o of p.przeszkody) {
        if (o.rodzaj === 'pod_dachem') continue
        const z = zakresPunktow(o.plan)
        przeszkodyX.push([z.minX, z.maxX])
        przeszkodyY.push([z.minY, z.maxY])
      }
    }
  }
  zPrzeszkod(dane.polacie.filter((p) => p.moduly.length))
  zPrzeszkod(dane.polacie.filter((p) => !p.moduly.length))
  return { wierszeX: [...wierszeX, ...przeszkodyX], wierszeY: [...wierszeY, ...przeszkodyY] }
}

// Pole wielokąta bez znaku (wybór największego widocznego kawałka połaci).
const pole = (w) => Math.abs(w.reduce((s, a, i) => s + a[0] * w[(i + 1) % w.length][1] - w[(i + 1) % w.length][0] * a[1], 0)) / 2
const ramkaPunktow = (w) => [Math.min(...w.map((q) => q[0])), Math.min(...w.map((q) => q[1])), Math.max(...w.map((q) => q[0])), Math.max(...w.map((q) => q[1]))]
const nachodza = (a, b) => a[0] < b[2] && b[0] < a[2] && a[1] < b[3] && b[1] < a[3]
const wSrodku = ([x, y], w) => {
  let wyn = false
  for (let i = 0, j = w.length - 1; i < w.length; j = i, i += 1) {
    if ((w[i][1] > y) !== (w[j][1] > y) && x < ((w[j][0] - w[i][0]) * (y - w[i][1])) / (w[j][1] - w[i][1]) + w[i][0]) wyn = !wyn
  }
  return wyn
}

/**
 * Opis w środku połaci (strzałka spadku i napis za nią): pierwsze miejsce z siatki punktów kawałka połaci, najbliżej
 * jego środka, w którym napis i strzałka nie zachodzą na moduły ani przeszkody. Bez wolnego miejsca: środek.
 */
const miejsceWSrodku = (kawalek, s, tekst, zajete) => {
  const r = ramkaPunktow(kawalek)
  const c = srodekWielokata(kawalek)
  const w = tekst.length * 1.5
  const ramka = ([x, y]) => {
    const tx = x - s[0] * 2.6
    const ty = y - s[1] * 2.6 + 0.9
    const k = [x + s[0] * DLUGOSC_STRZALKI, y + s[1] * DLUGOSC_STRZALKI]
    return [Math.min(tx - w / 2, x, k[0]) - 0.5, Math.min(ty - 2.6, y, k[1]) - 0.5, Math.max(tx + w / 2, x, k[0]) + 0.5, Math.max(ty + 0.6, y, k[1]) + 0.5]
  }
  const kandydaci = []
  for (let i = 1; i < 12; i += 1) {
    for (let j = 1; j < 12; j += 1) {
      const q = [r[0] + ((r[2] - r[0]) * i) / 12, r[1] + ((r[3] - r[1]) * j) / 12]
      if (wSrodku(q, kawalek)) kandydaci.push(q)
    }
  }
  kandydaci.sort((a, b) => Math.hypot(a[0] - c[0], a[1] - c[1]) - Math.hypot(b[0] - c[0], b[1] - c[1]))
  return kandydaci.find((q) => zajete.every((z) => !nachodza(ramka(q), z))) || c
}

// Opisy połaci przy okapie: strzałka spadku na zewnątrz i „A 38°”; połać bez widocznego okapu (górna część
// mansardy) ma opis w środku na białym tle; dach płaski tylko nazwę pod dolną krawędzią.
const etykietyPolaci = (dane, naPapier) => dane.polacie.filter((p) => !p.grunt && p.czesci.length).map((p) => {
  const najwiekszy = p.czesci.reduce((a, b) => (pole(b) > pole(a) ? b : a))
  const c = srodekWielokata(najwiekszy.map(naPapier))
  const odcinki = (p.krawedzieZGory || p.krawedzie).map((e) => ({ ...e, a: naPapier(e.plan[0]), b: naPapier(e.plan[1]) }))
  const dlugosc = (e) => Math.hypot(e.b[0] - e.a[0], e.b[1] - e.a[1])
  if (p.plaska) {
    const dolna = odcinki.reduce((best, e) => ((e.a[1] + e.b[1]) > (best.a[1] + best.b[1]) ? e : best))
    return { tekst: `${p.litera}, dach płaski`, x: (dolna.a[0] + dolna.b[0]) / 2, y: (dolna.a[1] + dolna.b[1]) / 2 + 4, wyrownanie: 'srodek', strzalka: null }
  }
  const tekst = `${p.litera} ${p.nachylenie}°`
  const okapy = odcinki.filter((e) => e.rodzaj === 'okap' && dlugosc(e) > 2)
  if (!okapy.length) {
    const s = p.spadek
    const zajete = dane.polacie.flatMap((q) => [
      ...q.moduly.map((m) => ramkaPunktow(m.plan.map(naPapier))),
      ...q.przeszkody.filter((o) => o.rodzaj !== 'pod_dachem').map((o) => ramkaPunktow(o.plan.map(naPapier))),
    ])
    const [x, y] = miejsceWSrodku(najwiekszy.map(naPapier), s, tekst, zajete)
    return {
      tekst, x: x - s[0] * 2.6, y: y - s[1] * 2.6 + 0.9, wyrownanie: 'srodek', tlo: true,
      strzalka: { x, y, kierunek: s, dlugosc: DLUGOSC_STRZALKI },
    }
  }
  const e = okapy.reduce((best, x) => (dlugosc(x) > dlugosc(best) ? x : best))
  const m = [(e.a[0] + e.b[0]) / 2, (e.a[1] + e.b[1]) / 2]
  const d = dlugosc(e) || 1
  let n = [-(e.b[1] - e.a[1]) / d, (e.b[0] - e.a[0]) / d]
  if (n[0] * (m[0] - c[0]) + n[1] * (m[1] - c[1]) < 0) n = [-n[0], -n[1]]
  const start = [m[0] + n[0] * 1.5, m[1] + n[1] * 1.5]
  const strzalka = { x: start[0], y: start[1], kierunek: n, dlugosc: DLUGOSC_STRZALKI }
  if (Math.abs(n[1]) >= Math.abs(n[0])) {
    const y = n[1] > 0 ? start[1] + DLUGOSC_STRZALKI + 2.6 : start[1] - DLUGOSC_STRZALKI - 0.8
    return { tekst, x: start[0], y, wyrownanie: 'srodek', strzalka }
  }
  return { tekst, x: start[0], y: start[1] - 1.2, wyrownanie: n[0] > 0 ? 'lewo' : 'prawo', strzalka }
})

/** Opis arkusza rzutu dachu z góry. */
export const opisRzutu = (dane, { format = FORMAT_DOMYSLNY } = {}) => {
  // Gdy napis szerokości modułu nie mieści się nad modułem (1:200), rzut podaje ciągi modułów, nie każdy moduł.
  const bok = Math.min(dane.modul.szerokosc, dane.modul.dlugosc)
  const wiersze = (skala) => wierszeRzutu(dane, { zwarte: bok * mmNaMetr(skala) < szerokoscCyfr(String(Math.round(bok * 1000))) + 1 })
  const a = rozmiescArkusz({ format, zakres: dane.zakres, wiersze, maks: MAKS_PASM_RZUTU })
  const grunt = dane.rodzaj === 'grunt'
  const zModulami = dane.polacie.filter((p) => p.moduly.length)
  const pochyle = zModulami.some((p) => !p.plaska)
  return {
    rodzaj: 'rzut',
    rysujTresc(r) { rysujTrescRzutu(r, this) },
    tytul: grunt ? 'Rzut instalacji na gruncie' : 'Rzut dachu',
    ...a,
    polacie: dane.polacie,
    kosze: dane.kosze || [],
    etykiety: etykietyPolaci(dane, a.naPapier),
    polnoc: { x: a.uklad.rysunek.x + 5, y: a.uklad.rysunek.y + a.uklad.rysunek.h - 5, kat: dane.polnoc },
    // Strefa „pod wyższym dachem” leży pod dachem innego skrzydła, więc z góry jej nie widać: tylko na arkuszu połaci.
    legenda: legenda(dane.polacie, { modul: dane.modul, spadek: dane.polacie.some((p) => !p.plaska && !p.grunt), bezPodDachem: true }),
    opis: {
      naglowek: 'Instalacja',
      wiersze: [
        wierszInstalacji(dane),
        ...zModulami.map(wierszPolaci),
        'Wymiary w mm.',
        ...(pochyle ? ['Na rzucie z góry długości wzdłuż spadku są skrócone. Prawdziwe długości na arkuszach połaci.'] : []),
      ],
    },
  }
}

/** Opis arkusza jednej połaci w prawdziwych wymiarach (widok prostopadły z zewnątrz, okap na dole). */
export const opisPolaci = (dane, p, { format = FORMAT_DOMYSLNY } = {}) => {
  // Na arkuszu połaci X = u, Y = −v (v rośnie w górę spadku, kartka w dół).
  const obrys = p.czesciUv.flat().map(([u, v]) => [u, -v])
  const zakres = zakresPunktow(obrys)
  const wierszeX = p.rzedy.map((r) => [...r.moduly.flatMap((m) => [m.uv[0], m.uv[2]]), ...(r.przekroj || [])])
  const wierszeY = [[...p.rzedy.flatMap((r) => [-r.vb, -r.va]), zakres.minY, zakres.maxY]]
  for (const o of p.przeszkody) {
    if (o.rodzaj === 'pod_dachem') continue
    const z = zakresPunktow(o.uv)
    wierszeX.push([z.minX, z.maxX])
    wierszeY.push([-z.maxY, -z.minY])
  }
  const a = rozmiescArkusz({ format, zakres, wiersze: () => ({ wierszeX, wierszeY }), naDole: true, maks: MAKS_PASM_POLACI, pierscien: PIERSCIEN_POLACI })
  const naPapierUv = ([u, v]) => a.naPapier([u, -v])
  const c = srodekWielokata(p.czesciUv.flat().map(naPapierUv))
  const krawedzie = [...p.krawedzie, ...p.kosze.map((uv) => ({ rodzaj: 'kosz', uv }))].map((e) => {
    const [A, B] = e.uv.map(naPapierUv)
    const d = Math.hypot(B[0] - A[0], B[1] - A[1]) || 1
    const m = [(A[0] + B[0]) / 2, (A[1] + B[1]) / 2]
    let n = [-(B[1] - A[1]) / d, (B[0] - A[0]) / d]
    if (n[0] * (m[0] - c[0]) + n[1] * (m[1] - c[1]) < 0) n = [-n[0], -n[1]]
    let kat = (Math.atan2(-(B[1] - A[1]), B[0] - A[0]) * 180) / Math.PI
    if (kat > 90.5) kat -= 180
    if (kat <= -89.5) kat += 180
    // Napis po zewnętrznej stronie krawędzi: gdy litery rosną do środka połaci, linię bazową odsuwamy o ich wysokość.
    const wGore = [-Math.sin((kat * Math.PI) / 180), -Math.cos((kat * Math.PI) / 180)]
    const odsuniecie = wGore[0] * n[0] + wGore[1] * n[1] > 0 ? 1.2 : 3.2
    // Podpis tylko na krawędzi dłuższej niż napis (ok. 1,3 mm na znak przy 6,3 pt).
    const tekst = d > (NAZWY_KRAWEDZI[e.rodzaj] || '').length * 1.3 + 2 ? NAZWY_KRAWEDZI[e.rodzaj] || '' : ''
    return { rodzaj: e.rodzaj, a: A, b: B, tekst, x: m[0] + n[0] * odsuniecie, y: m[1] + n[1] * odsuniecie, kat }
  })
  const dlugoscKrawedzi = (rodzaj) => p.krawedzie.filter((e) => e.rodzaj === rodzaj).reduce((s, e) => s + Math.hypot(e.uv[1][0] - e.uv[0][0], e.uv[1][1] - e.uv[0][1]), 0)
  // Górna część mansardy nie ma okapu: jej dolną krawędzią jest załamanie.
  const okap = dlugoscKrawedzi('okap') || dlugoscKrawedzi('zalamanie')
  const nazwaDolnej = dlugoscKrawedzi('okap') ? 'Okap' : 'Załamanie'
  const numery = p.moduly.map((m) => m.nr)
  const n = p.moduly.length
  return {
    rodzaj: 'polac',
    rysujTresc(r) { rysujTrescPolaci(r, this) },
    tytul: `Połać ${p.litera}, prawdziwe wymiary`,
    ...a,
    polac: p,
    krawedzie,
    legenda: legenda([p], { modul: dane.modul, odstep: p.obszarRoboczy.length ? dane.margines : null }),
    opis: {
      naglowek: `Połać ${p.litera}`,
      wiersze: [
        `${p.kierunek}, azymut ${p.azymut}°, kąt ${p.nachylenie}°`,
        `${n} ${modulow(n)} (nr ${Math.min(...numery)}–${Math.max(...numery)}), ${liczba((n * dane.modul.moc) / 1000)} kWp`,
        `${nazwaDolnej} ${Math.round(okap * 1000)} mm, wzdłuż spadku ${Math.round((zakres.maxY - zakres.minY) * 1000)} mm`,
        'Widok prostopadły na połać z zewnątrz, okap na dole. Prawdziwe długości w mm.',
      ],
    },
  }
}

/**
 * Opis całego rysunku: rzut dachu i arkusze połaci z modułami. `opcje` = { format, data, nrTeczki, dopisek }.
 */
export const opisRysunkuDachu = (dane, { format = FORMAT_DOMYSLNY, data = dataRysunku(), nrTeczki = '', dopisek = DOPISEK_RYSUNKU } = {}) => {
  const arkusze = [
    opisRzutu(dane, { format }),
    ...dane.polacie.filter((p) => p.moduly.length && !p.plaska && p.uv).map((p) => opisPolaci(dane, p, { format })),
  ]
  ponumerujArkusze(arkusze, { data, nrTeczki, dopisek })
  return { tytul: dane.rodzaj === 'grunt' ? 'Rysunek instalacji' : 'Rysunek dachu', format, arkusze }
}

/* ═══ Rysowanie ════════════════════════════════════════════════════════════════════════════════════ */

const rysujTrescRzutu = (r, a) => {
  const P = a.naPapier
  for (const p of a.polacie) {
    if (p.grunt) { if (p.plan.length) r.wielokat(p.plan.map(P), { grubosc: 0.35 }); continue }
    for (const e of p.krawedzieZGory || p.krawedzie) {
      const [A, B] = e.plan.map(P)
      r.linia(A[0], A[1], B[0], B[1], { grubosc: e.rodzaj === 'okap' || e.rodzaj === 'szczytowa' ? 0.35 : 0.25 })
    }
  }
  for (const k of a.kosze) {
    const [A, B] = k.map(P)
    r.linia(A[0], A[1], B[0], B[1], { grubosc: 0.25 })
  }
  for (const p of a.polacie) for (const o of p.przeszkody) if (o.strefaPlan && o.rodzaj !== 'pod_dachem') rysujStrefe(r, o.strefaPlan.map(P))
  for (const p of a.polacie) {
    for (const o of p.przeszkody) {
      if (o.rodzaj === 'pod_dachem') continue
      rysujPrzeszkodeRodzaju(r, o.rodzaj, o.plan.map(P), { linie: (o.liniePlan || []).map((l) => l.map(P)), krok: KROK_PASA * a.k })
    }
  }
  for (const p of a.polacie) for (const m of p.moduly) rysujModul(r, m.plan.map(P), { nr: m.nr, rama: RAMA_MODULU * a.k })
  for (const e of a.etykiety) {
    if (e.tlo) {
      const w = r.szer(e.tekst, 7)
      r.prostokat(e.x - w / 2 - 0.8, e.y - 2.8, w + 1.6, 3.7, { grubosc: 0, wypelnienie: r.kolory.bialy })
    }
    if (e.strzalka) rysujStrzalke(r, e.strzalka.x, e.strzalka.y, e.strzalka.kierunek, e.strzalka.dlugosc)
    r.tekst(e.tekst, e.x, e.y, { rozmiar: 7, wyrownanie: e.wyrownanie })
  }
  rysujPolnoc(r, a.polnoc.x, a.polnoc.y, a.polnoc.kat)
}

const rysujTrescPolaci = (r, a) => {
  const P = ([u, v]) => a.naPapier([u, -v])
  const p = a.polac
  for (const e of a.krawedzie) {
    r.linia(e.a[0], e.a[1], e.b[0], e.b[1], { grubosc: e.rodzaj === 'okap' || e.rodzaj === 'szczytowa' ? 0.35 : 0.25 })
    r.tekst(e.tekst, e.x, e.y, { rozmiar: 6.3, wyrownanie: 'srodek', obrot: e.kat, kolor: r.kolory.szary })
  }
  if (p.obszarRoboczy.length) rysujOdstep(r, p.obszarRoboczy.map(P))
  for (const o of p.przeszkody) if (o.strefaUv && o.rodzaj !== 'pod_dachem') rysujStrefe(r, o.strefaUv.map(P))
  for (const o of p.przeszkody) {
    rysujPrzeszkodeRodzaju(r, o.rodzaj, o.uv.map(P), { linie: (o.linieUv || []).map((l) => l.map(P)), krok: KROK_PASA * a.k })
  }
  for (const m of p.moduly) {
    const [u0, v0, u1, v1] = m.uv
    rysujModul(r, [[u0, v0], [u1, v0], [u1, v1], [u0, v1]].map(P), { nr: m.nr, rama: RAMA_MODULU * a.k })
  }
}

/**
 * Rysuje opis i zwraca bajty PDF (wspólne składanie arkuszy w `arkusze.js`). `biblioteki` = { PDFDocument, rgb,
 * degrees } z pdf-lib i `fontkit`, `czcionka` = bajty TTF z polskimi znakami.
 */
export const narysujRysunekDachu = (opis, biblioteki, czcionka) => narysujArkusze(opis, biblioteki, czcionka)

/** W przeglądarce: pdf-lib i fontkit ładowane dopiero przy generowaniu, czcionka z `public/osd/`. */
export const rysujRysunekDachu = (dane, opcje, urlCzcionki) => rysujArkusze(opisRysunkuDachu(dane, opcje), urlCzcionki)
